import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/opt/atlassian/pipelines/agent/build/platform/services/design-system-docs/src/__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/templates/page.tsx";
import { Stack } from '@atlaskit/primitives';
import { IconCardGroup } from '@af/design-system-docs-ui';
import { VideoPlayer } from '../../../components/video-player';
import { GuidanceCard } from '../../../components/guidance-cards/guidance-cards';
import { guidanceCardFoundations, guidanceCardComponents, guidanceCardContent } from '../../../components/guidance-cards/guidance-card-content';
import { contactUsCards } from '../../contact-us';
import { QuoteCard, quoteCard1, quoteCard2, quoteCard3 } from '../../../components/quote-cards/quote-cards';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h2 {...{
      "id": "the-parts-of-the-design-system"
    }}>{`The parts of the design system`}</h2>
    <p>{`The Atlassian Design System is a collection of design guidelines, foundations, tools, and
components. It is built and supported by Atlassian developers, designers, and content designers.
It's used internally by Atlassians, Marketplace partners, and other external users.`}</p>
    <Stack space="space.150" mdxType="Stack">
	<GuidanceCard card={guidanceCardFoundations} mdxType="GuidanceCard" />
	<GuidanceCard card={guidanceCardComponents} mdxType="GuidanceCard" />
    </Stack>
    <h2 {...{
      "id": "what-people-say-about-the-design-system"
    }}>{`What people say about the design system`}</h2>
    <Stack space="space.150" mdxType="Stack">
	<QuoteCard card={quoteCard1} mdxType="QuoteCard" />
	<QuoteCard card={quoteCard2} isInverted mdxType="QuoteCard" />
	<QuoteCard card={quoteCard3} mdxType="QuoteCard" />
    </Stack>
    <h2 {...{
      "id": "benefits-of-using-the-atlassian-design-system"
    }}>{`Benefits of using the Atlassian Design System`}</h2>
    <h3 {...{
      "id": "1-scalability-across-apps"
    }}>{`1. Scalability across apps`}</h3>
    <p>{`The design system makes it simpler to scale. Centralized, reusable, and dependable components align
designers and developers, ensuring consistency and quality.`}</p>
    <h3 {...{
      "id": "2-efficiency-to-build-and-maintain-apps"
    }}>{`2. Efficiency to build and maintain apps`}</h3>
    <p>{`Designers save time using prebuilt elements, while developers build more efficiently with reusable
code. This makes fixing or updating apps post-launch quicker and easier.`}</p>
    <h3 {...{
      "id": "3-consistency-of-brand-and-design"
    }}>{`3. Consistency of brand and design`}</h3>
    <p>{`Consistency is key to great user experiences. Our design system standardizes patterns and
components, ensuring a unified experience across platforms and devices.`}</p>
    <h3 {...{
      "id": "4-preservation-of-design-decisions"
    }}>{`4. Preservation of design decisions`}</h3>
    <p>{`When knowledge isn't documented, teams risk losing it during staff changes. Our design system stores
all design decisions in a centralized, accessible place.`}</p>
    <h3 {...{
      "id": "5-effective-knowledge-sharing"
    }}>{`5. Effective knowledge sharing`}</h3>
    <p>{`With clear documentation, teams can onboard quickly, collaborate better, and align on guidelines
easily. Our design system acts as a central resource for all design decisions.`}</p>
    <h2 {...{
      "id": "our-vision"
    }}>{`Our vision`}</h2>
    <p>{`Our vision is to be the cornerstone of crafting quality user interfaces in Atlassian apps. We're on
a mission to make it easy for teams to design, develop, and deliver high-quality Atlassian UI.`}</p>
    <VideoPlayer url="https://www.youtube-nocookie.com/embed/3QH2T9dUJz4" title="Reimagining Atlassian Design System: A vision | Atlassian" mdxType="VideoPlayer" />
    <h2 {...{
      "id": "our-values"
    }}>{`Our values`}</h2>
    <p>{`Our values drive us to create things that are foundational, harmonious, and empowering to use.`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Foundational:`}</strong>{` We solve common problems and provide a foundation for teams to build on. We
avoid consistency for the sake of consistency and reject infinite flexibility.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Harmonious:`}</strong>{` Our building blocks work together to create apps products that feel familiar,
cohesive, and part of a family. We build trust through consistency and purpose.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Empowering, for everyone:`}</strong>{` We strive to make our design system accessible to work for everyone
who relies on it, regardless of discipline their role, experience, or skill level, or tenure. By
enabling as many making it easy for more people as possible to use the system, we expand multiply
our impact.`}</p>
      </li>
    </ol>
    <h2 {...{
      "id": "our-principles"
    }}>{`Our principles`}</h2>
    <p>{`Our principles guide our actions to align with our values.`}</p>
    <h3 {...{
      "id": "1-trusted-fundamentals-before-comprehensive-patterns"
    }}>{`1. Trusted fundamentals before comprehensive patterns`}</h3>
    <ul>
      <li parentName="ul">{`We solve common foundational problems first, enabling teams to focus on whole product experiences.`}</li>
      <li parentName="ul">{`We provide opinionated building blocks, allowing designers and developers to compose complex
experiences that remain distinctly Atlassian.`}</li>
      <li parentName="ul">{`We build with heart and balance, ensuring quality through research and clear standards for
accessibility, responsiveness, and enterprise scale.`}</li>
    </ul>
    <h3 {...{
      "id": "2-meet-system-needs-before-delivering-individual-features"
    }}>{`2. Meet system needs before delivering individual features`}</h3>
    <ul>
      <li parentName="ul">{`We don't stop at shipping — documentation, support, tooling, and maintenance are part of a
harmonious experience.`}</li>
      <li parentName="ul">{`We bring in many perspectives to shape our design language.`}</li>
      <li parentName="ul">{`We finish what we start before starting something new. We're informed by continuous feedback.`}</li>
    </ul>
    <h3 {...{
      "id": "3-bring-people-on-the-journey-before-helping-for-the-moment"
    }}>{`3. Bring people on the journey before helping for the moment`}</h3>
    <ul>
      <li parentName="ul">{`We bring teams and products apps into our process to build trust, share understanding, and
co-create solutions.`}</li>
      <li parentName="ul">{`We prioritize the best service experience. We provide the right amount of resources, support, and
context.`}</li>
      <li parentName="ul">{`We optimize for self-service. We help teams make decisions independently and with confidence. We
want everyone using our system to become a design system champion.`}</li>
    </ul>
    <h2 {...{
      "id": "contact-us"
    }}>{`Contact us`}</h2>
    <IconCardGroup data={contactUsCards} mdxType="IconCardGroup" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      